import { useEffect, useState } from "react";
import { toast } from "sonner";
import { RotateCcw } from "lucide-react";
import api from "@/lib/api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const FONTS = ["Montserrat", "Jost", "Marcellus", "Playfair Display", "Cormorant Garamond"];

const ACTIVITIES = [
  { key: "univers", label: "Univers Maasaï" },
  { key: "palais", label: "Palais Maasaï" },
  { key: "comptoir", label: "Le Comptoir Maasaï" },
  { key: "services", label: "Maasaï Services" },
];

const COLOR_FIELDS = [
  { key: "copper", label: "Couleur principale (boutons, accents)" },
  { key: "copperdark", label: "Couleur principale — survol" },
  { key: "gold", label: "Couleur dorée (titres, prix)" },
  { key: "ink", label: "Fond du site" },
  { key: "coal", label: "Fond des cartes" },
  { key: "ivory", label: "Couleur des textes" },
];

const DEFAULT_THEME = {
  copper: "#C4704F",
  copperdark: "#A85B3D",
  gold: "#C6A87C",
  ink: "#050505",
  coal: "#0F0F0F",
  ivory: "#F5F5F0",
  radius: "0.5rem",
  font: "Montserrat",
};

export default function ThemesManager() {
  const [design, setDesign] = useState(null);
  const [active, setActive] = useState("univers");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get("/settings")
      .then((r) => setDesign(r.data?.design || {}))
      .catch(() => setDesign({}));
  }, []);

  if (!design) return null;

  const themes = design.themes || {};
  const theme = { ...DEFAULT_THEME, ...themes[active] };
  const isCustomized = !!themes[active];

  const updateTheme = (key, value) => {
    setDesign((d) => ({
      ...d,
      themes: {
        ...(d.themes || {}),
        [active]: { ...DEFAULT_THEME, ...(d.themes?.[active] || {}), [key]: value },
      },
    }));
  };

  const resetTheme = () => {
    setDesign((d) => {
      const next = { ...(d.themes || {}) };
      delete next[active];
      return { ...d, themes: next };
    });
    toast.success("Thème réinitialisé (le style global du site s'appliquera).");
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", { design });
      toast.success("Thèmes enregistrés. Rechargez le site pour les voir partout.");
    } catch {
      toast.error("Erreur lors de l'enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl" data-testid="themes-manager">
      <h2 className="mb-2 text-lg font-semibold text-ivory">Thèmes graphiques</h2>
      <p className="mb-8 text-xs leading-relaxed text-ivory/40">
        Chaque activité peut avoir son propre thème (couleurs, arrondis, police). Sans
        personnalisation, le style global du site (onglet Design) s'applique.
      </p>

      <div className="mb-8 flex flex-wrap gap-2" data-testid="themes-activity-tabs">
        {ACTIVITIES.map((a) => (
          <button
            key={a.key}
            onClick={() => setActive(a.key)}
            data-testid={`theme-tab-${a.key}`}
            className={`rounded-md border px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.15em] transition-colors ${
              active === a.key
                ? "border-copper bg-copper text-ivory"
                : "border-white/10 text-ivory/60 hover:border-copper/50 hover:text-ivory"
            }`}
          >
            {a.label}
            {themes[a.key] && <span className="ml-2 text-gold">●</span>}
          </button>
        ))}
      </div>

      <div className="space-y-8 rounded-lg border border-white/10 bg-coal/60 p-8" data-testid="theme-editor">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold uppercase tracking-[0.2em] text-gold">
            {ACTIVITIES.find((a) => a.key === active)?.label}
          </h3>
          {isCustomized && (
            <button onClick={resetTheme} className="flex items-center gap-2 text-xs text-ivory/40 transition-colors hover:text-red-400" data-testid="theme-reset-button">
              <RotateCcw className="h-3.5 w-3.5" strokeWidth={1.5} />
              Réinitialiser
            </button>
          )}
        </div>

        <div className="grid gap-5 sm:grid-cols-2">
          {COLOR_FIELDS.map((c) => (
            <div key={c.key} className="flex items-center gap-4">
              <input
                type="color"
                value={theme[c.key]}
                onChange={(e) => updateTheme(c.key, e.target.value)}
                className="h-11 w-14 shrink-0 cursor-pointer rounded-md border border-white/10 bg-white/5 p-1"
                data-testid={`theme-color-${c.key}`}
              />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm text-ivory/80">{c.label}</p>
                <input
                  value={theme[c.key]}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (/^#[0-9A-Fa-f]{0,6}$/.test(v)) updateTheme(c.key, v);
                  }}
                  className="mt-1 w-24 rounded border border-white/10 bg-white/5 px-2 py-0.5 font-mono text-xs uppercase text-ivory/60 outline-none focus:border-copper"
                  data-testid={`theme-hex-${c.key}`}
                />
              </div>
            </div>
          ))}
        </div>

        <div className="grid gap-6 sm:grid-cols-2">
          <div>
            <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-ivory/50">
              Police
            </label>
            <Select value={theme.font} onValueChange={(v) => updateTheme("font", v)}>
              <SelectTrigger className="!h-11 border-white/10 bg-white/5 text-sm text-ivory" data-testid="theme-font-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="border-white/10 bg-coal text-ivory">
                {FONTS.map((f) => (
                  <SelectItem key={f} value={f}>{f}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-ivory/50">
              Arrondi des coins — {theme.radius}
            </label>
            <input
              type="range"
              min="0"
              max="1.5"
              step="0.05"
              value={parseFloat(theme.radius)}
              onChange={(e) => updateTheme("radius", `${e.target.value}rem`)}
              className="mt-3 w-full accent-[#C4704F]"
              data-testid="theme-radius"
            />
          </div>
        </div>

        <div>
          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.15em] text-ivory/50">
            Aperçu
          </p>
          <div className="rounded-lg p-6" style={{ backgroundColor: theme.ink }} data-testid="theme-preview">
            <div className="rounded-lg border border-white/10 p-5" style={{ backgroundColor: theme.coal, borderRadius: theme.radius }}>
              <p className="text-xs font-semibold uppercase tracking-[0.3em]" style={{ color: theme.gold, fontFamily: theme.font }}>
                Survol doré
              </p>
              <p className="mt-2 text-sm" style={{ color: theme.ivory, fontFamily: theme.font }}>
                Texte principal du thème {ACTIVITIES.find((a) => a.key === active)?.label}.
              </p>
              <button
                className="mt-4 px-5 py-2 text-[11px] font-semibold uppercase tracking-[0.15em]"
                style={{ backgroundColor: theme.copper, color: theme.ivory, borderRadius: theme.radius, fontFamily: theme.font }}
              >
                Bouton principal
              </button>
            </div>
          </div>
        </div>
      </div>

      <button onClick={save} disabled={saving} className="btn-primary mt-8" data-testid="themes-save-button">
        {saving ? "Enregistrement…" : "Enregistrer les thèmes"}
      </button>
    </div>
  );
}
